import React, { useState, useEffect } from 'react';
import { Modal } from '../common/Modal';
import { Editor } from './Editor';
import { Trash2 } from 'lucide-react';

interface News {
  id: string;
  title: string;
  content: string;
  createdAt: Date;
  updatedAt?: Date;
  author: {
    id: string;
    name: string;
  };
  important?: boolean;
  pinned?: boolean;
}

interface CreateNewsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: Omit<News, 'id' | 'createdAt' | 'author'>) => Promise<void>;
  initialData?: News | null;
  onDelete?: () => Promise<void>;
}

export const CreateNewsModal: React.FC<CreateNewsModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  initialData,
  onDelete
}) => {
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    important: false,
    pinned: false
  });
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (initialData) {
      setFormData({
        title: initialData.title || '',
        content: initialData.content || '',
        important: initialData.important || false,
        pinned: initialData.pinned || false
      });
    } else {
      setFormData({
        title: '',
        content: '',
        important: false,
        pinned: false
      });
    }
  }, [initialData]);
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (loading) return;

    setLoading(true);
    setError(null);

    try {
      await onSubmit(formData);
      onClose();
    } catch (error: any) {
      setError(error.message || 'Ein Fehler ist aufgetreten');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!onDelete || loading) return;

    setLoading(true);
    setError(null);

    try {
      await onDelete();
      onClose();
    } catch (error: any) {
      setError(error.message || 'Ein Fehler ist aufgetreten');
    } finally {
      setLoading(false);
    }
  };

  const renderFooter = () => (
    <div className="flex flex-col-reverse sm:flex-row sm:justify-between gap-3">
      <div className="flex justify-start">
        {onDelete && (
          <button
            type="button"
            onClick={handleDelete}
            disabled={loading}
            className="px-4 py-2 text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 disabled:opacity-50"
          >
            <Trash2 className="h-5 w-5" />
          </button>
        )}
      </div>
      <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-3">
        <button
          type="button"
          onClick={onClose}
          disabled={loading}
          className="w-full sm:w-auto px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
        >
          Abbrechen
        </button>
        <button
          type="submit"
          form="news-form"
          disabled={loading}
          className="w-full sm:w-auto px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 dark:bg-indigo-500 dark:hover:bg-indigo-600 transition-colors disabled:opacity-50"
        >
          {loading ? 'Wird gespeichert...' : initialData ? 'Aktualisieren' : 'Erstellen'}
        </button>
      </div>
    </div>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={initialData ? 'Meldung bearbeiten' : 'Neue Meldung'}
      footer={renderFooter()}
      size="xl"
    >
      <form id="news-form" onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
            Titel
          </label>
          <input
            type="text"
            value={formData.title}
            onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
            className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:bg-gray-700 dark:text-white"
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Inhalt
          </label>
          <Editor
            content={initialData?.content || formData.content}
            onChange={(content) => setFormData(prev => ({ ...prev, content }))}
            placeholder="Schreiben Sie hier Ihre Nachricht..."
          />
        </div>

        <div className="flex items-center space-x-4">
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={formData.important}
              onChange={(e) => setFormData(prev => ({ ...prev, important: e.target.checked }))}
              className="rounded border-gray-300 dark:border-gray-600 text-indigo-600 focus:ring-indigo-500 dark:bg-gray-700"
            />
            <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
              Als wichtig markieren
            </span>
          </label>

          <label className="flex items-center">
            <input
              type="checkbox"
              checked={formData.pinned}
              onChange={(e) => setFormData(prev => ({ ...prev, pinned: e.target.checked }))}
              className="rounded border-gray-300 dark:border-gray-600 text-indigo-600 focus:ring-indigo-500 dark:bg-gray-700"
            />
            <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
              Oben anpinnen
            </span>
          </label>
        </div>

        {error && (
          <div className="rounded-md bg-red-50 dark:bg-red-900/50 p-4">
            <p className="text-sm text-red-700 dark:text-red-200">{error}</p>
          </div>
        )}
      </form>
    </Modal>
  );
};
